import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {isTransmissionExcluded} from '../../../../../constants/transmissionOptionsExcluded';
import {
  GetTrimLevelsApiResponse,
  NumericBoundBody,
  RelativeFilterChoice,
  RelativeFilterConfigBody,
} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {convertToPercentage} from '../../../../../utils/convertToPercentage';
import {getSimilarBodyStyles} from '../../../../../utils/getSimilarBodyStyles';
import {getBetterOrEqualValues} from './getBetterOrEqualValues';
import {getSameAndBetterTrimLevelConstKeys} from './getSameAndBetterTrimLevelConstKeys';

/**
 * Inverse of the preset field resolvers: derives a relative filter config from the
 * currently applied absolute filter values, so a custom filter can be saved as a
 * preset with the editor prefilled. Best effort — selections that cannot be
 * expressed relatively (e.g. an arbitrary fuel mix) fall back to null (“any”).
 */
export function buildRelativeConfigFromFormValues(
  formValues: FilterFormData,
  priceReport: PriceReportType,
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null
): RelativeFilterConfigBody {
  const {vehicle} = priceReport;
  const recommendedPrice = priceReport.sellingPrice.recommended.value ?? null;
  const rawFeatureLevel =
    priceReport.featuresLevel ?? priceReport.additionalSettings?.featuresLevel ?? null;
  const featureScoreBase = resolveFeatureScoreBase(rawFeatureLevel);
  const vehiclePower = vehicle.maxPower ?? vehicle.power ?? null;
  const vehicleVat: 'YES' | 'NO' = vehicle.isVatDeductible === true ? 'YES' : 'NO';

  return {
    year: offsetBound(
      formValues.firstRegistrationOnYearGte,
      formValues.firstRegistrationOnYearLte,
      vehicle.firstRegistrationOnYear
    ),
    mileage: offsetBound(formValues.mileageGte, formValues.mileageLte, vehicle.mileage ?? 0),
    mileagePct: null,
    power: offsetBound(formValues.powerGte, formValues.powerLte, vehiclePower),
    powerPct: null,
    price: offsetBound(formValues.priceGte, formValues.priceLte, recommendedPrice),
    pricePct: null,
    featureScore: offsetBound(
      formValues.featureScoreGte,
      formValues.featureScoreLte,
      featureScoreBase
    ),
    trimLevel: deriveTrimLevelChoice(formValues.trimLevels ?? [], vehicle, trimLevelsCatalog),
    transmission: deriveTransmissionChoice(formValues.transmission, vehicle),
    fuel: sameSet(formValues.fuelType, [vehicle.fuelType]) ? 'same' : null,
    body: sameSet(formValues.bodyStyle, getSimilarBodyStyles(vehicle.bodyStyle)) ? 'same' : null,
    drive: deriveOrderedChoice(formValues.drive, 'drive', vehicle.drive),
    vatDeductible: deriveOrderedChoice(formValues.isVatDeductible, 'vat', vehicleVat),
    sellerType: isNotNilOrEmpty(formValues.sellerType) ? (formValues.sellerType ?? null) : null,
    seats: null,
    radius: formValues.radius ?? null,
    features: isNotNilOrEmpty(formValues.features) ? formValues.features : null,
  };
}

function resolveFeatureScoreBase(rawFeatureLevel: number | null): number | null {
  if (isNil(rawFeatureLevel)) {
    return null;
  }

  return rawFeatureLevel > 1 ? Math.round(rawFeatureLevel) : convertToPercentage(rawFeatureLevel);
}

function offsetBound(
  gte: number | Nullish,
  lte: number | Nullish,
  base: number | Nullish
): NumericBoundBody | null {
  if (isNil(base) || (isNil(gte) && isNil(lte))) {
    return null;
  }
  return {
    from: isNotNil(gte) ? gte - base : null,
    to: isNotNil(lte) ? lte - base : null,
  };
}

function sameSet(left: string[] | Nullish, right: string[]): boolean {
  if (isNil(left) || left.length === 0 || left.length !== right.length) {
    return false;
  }
  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
}

function deriveOrderedChoice(
  selected: string[] | Nullish,
  group: 'drive' | 'vat',
  vehicleValue: string | Nullish
): RelativeFilterChoice | null {
  if (isNil(selected) || selected.length === 0 || isNil(vehicleValue)) {
    return null;
  }
  if (sameSet(selected, [vehicleValue])) {
    return 'same';
  }
  if (sameSet(selected, getBetterOrEqualValues(group, vehicleValue))) {
    return 'same_better';
  }
  return null;
}

function deriveTransmissionChoice(
  selected: string[],
  vehicle: PriceReportType['vehicle']
): RelativeFilterChoice | null {
  if (selected.length === 0) {
    return null;
  }
  if (!isTransmissionExcluded(vehicle.transmission) && sameSet(selected, [vehicle.transmission])) {
    return 'same';
  }
  const sameAndBetter = getBetterOrEqualValues('transmission', vehicle.transmission).filter(
    (transmission) => !isTransmissionExcluded(transmission)
  );
  if (sameAndBetter.length > 0 && sameSet(selected, sameAndBetter)) {
    return 'same_better';
  }
  return null;
}

function deriveTrimLevelChoice(
  selected: string[],
  vehicle: PriceReportType['vehicle'],
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null
): RelativeFilterChoice | null {
  if (selected.length === 0 || isNil(vehicle.trimLevel)) {
    return null;
  }
  if (sameSet(selected, [vehicle.trimLevel])) {
    return 'same';
  }
  const sameAndBetter = getSameAndBetterTrimLevelConstKeys(trimLevelsCatalog, vehicle.trimLevel);
  if (sameAndBetter.length > 0 && sameSet(selected, sameAndBetter)) {
    return 'same_better';
  }
  return null;
}
